import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Group,
  Loader,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconMailForward, IconPencil, IconSearch, IconX } from '@tabler/icons-react';
import { PageHeader } from '../../shared/components/PageHeader';
import { ResponsiveTable } from '../../shared/components/ResponsiveTable';
import type { DataColumn } from '../../shared/components/ResponsiveTable';
import { ApiError } from '../../shared/api/problem';
import { usePermissions } from '../auth/usePermissions';
import {
  cancelInvitation,
  listInvitations,
  listUsers,
  type InvitationDto,
  type TenantUserDto,
} from './api';
import { UserAccessModal } from './UserAccessModal';
import { InviteUserModal } from './InviteUserModal';

/**
 * Who is in this tenant, what they may do, and which buildings they may do it in.
 *
 * The screen exists because the last of those had no writer at all: tbl_UserWarehouseAccess has
 * been in the schema since 001 and nothing ever wrote to it, so a second user in a tenant could
 * not be given access to anything. Tenant script 045 added the writer; this is the way in.
 *
 * NOTHING ENFORCES THE GRANTS YET. Recording them has to land before anything is gated on them —
 * every existing user holds zero, so enforcing first would lock everybody out on the deploy. The
 * screen therefore says what a grant currently means rather than implying it is a lock.
 */

/**
 * The server's own sentence, not a shrug.
 *
 * Every refusal from the user and group endpoints names what went wrong — which group is locked,
 * how many members block a delete, that a script is missing. These two screens were the only ones
 * showing a generic "Could not load", which on 2026-08-18 hid a stale API deployment behind four
 * identical words while the database was fine. A 404 or a 403 says something too, so the status is
 * included when the body carries no detail.
 */
function describeLoadFailure(err: unknown, fallback: string): string {
  if (!(err instanceof ApiError)) return fallback;
  const detail = err.problem.detail ?? err.problem.title;
  return detail ? `${fallback} ${detail}` : `${fallback} (HTTP ${err.status})`;
}

export function UsersRoute() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const permissions = usePermissions();

  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<TenantUserDto | null>(null);
  const [inviting, setInviting] = useState(false);

  const canManage = permissions.has('tenant:manage_users');

  const users = useQuery({
    queryKey: ['users'] as const,
    queryFn: ({ signal }) => listUsers(signal),
    enabled: canManage,
  });

  /**
   * Pending invitations only. An accepted one has become a user and is in the list above; a
   * cancelled or expired one is history. Showing them all would bury the two that matter.
   */
  const invitations = useQuery({
    queryKey: ['invitations'] as const,
    queryFn: ({ signal }) => listInvitations(false, signal),
    enabled: canManage,
  });

  const cancel = useMutation({
    mutationFn: (id: string) => cancelInvitation(id),
    onSuccess: () => {
      notifications.show({ message: t('users.inviteCancelled'), color: 'green' });
      void queryClient.invalidateQueries({ queryKey: ['invitations'] });
    },
    onError: (err) =>
      notifications.show({
        message: describeLoadFailure(err, t('users.errors.cancelInviteFailed')),
        color: 'red',
      }),
  });

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return users.data ?? [];
    return (users.data ?? []).filter(
      (u) =>
        u.email.toLowerCase().includes(needle) ||
        u.displayName.toLowerCase().includes(needle) ||
        u.activeGroupCodes.some((c) => c.toLowerCase().includes(needle)),
    );
  }, [users.data, search]);

  const columns: DataColumn<TenantUserDto>[] = useMemo(
    () => [
      {
        key: 'name',
        label: t('users.name'),
        card: 'title',
        render: (u) => u.displayName || u.email,
      },
      {
        key: 'email',
        label: t('users.email'),
        card: 'subtitle',
        render: (u) => u.email,
      },
      {
        key: 'status',
        label: t('users.status'),
        card: 'aside',
        render: (u) =>
          u.isActive ? (
            <Text size="sm" c="dimmed">
              {t('users.active')}
            </Text>
          ) : (
            <Badge size="sm" variant="light" color="gray">
              {t('users.inactive')}
            </Badge>
          ),
      },
      {
        key: 'groups',
        label: t('users.groups'),
        render: (u) =>
          u.activeGroupCodes.length === 0 ? (
            <Badge size="sm" variant="light" color="yellow">
              {t('users.noGroups')}
            </Badge>
          ) : (
            <Group gap={4} wrap="wrap" justify="flex-end">
              {u.activeGroupCodes.map((c) => (
                <Badge key={c} size="sm" variant="light" color="invetrak">
                  {c}
                </Badge>
              ))}
            </Group>
          ),
      },
      {
        key: 'warehouses',
        label: t('users.warehouses'),
        align: 'right',
        // Three answers, not a number. "All" rather than a count for anyone unscoped, because a
        // count would change under the reader every time somebody added a warehouse — and zero is
        // worth calling out, since it means they can sign in and do nothing.
        render: (u) =>
          u.hasAllWarehouses ? (
            <Badge size="sm" variant="light" color="blue">
              {t('users.allWarehouses')}
            </Badge>
          ) : u.warehouseCount === 0 ? (
            <Tooltip label={t('users.noWarehousesHint')}>
              <Badge size="sm" variant="light" color="yellow">
                {t('users.noWarehouses')}
              </Badge>
            </Tooltip>
          ) : (
            u.warehouseCount
          ),
      },
    ],
    [t],
  );

  if (!canManage) {
    return (
      <>
        <PageHeader title={t('nav.users')} />
        <Alert color="yellow" variant="light" icon={<IconAlertCircle size={16} />}>
          {t('users.notPermitted')}
        </Alert>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={t('nav.users')}
        actions={
          <Button
            color="invetrak"
            leftSection={<IconMailForward size={16} />}
            onClick={() => setInviting(true)}
          >
            {t('users.invite')}
          </Button>
        }
      />

      <Stack gap="md">
        <Alert color="blue" variant="light" icon={<IconAlertCircle size={16} />}>
          {t('users.intro')}
        </Alert>

        <Group gap="sm" align="flex-end" wrap="wrap">
          <TextInput
            label={t('users.search')}
            placeholder={t('users.searchPlaceholder')}
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            w={{ base: '100%', sm: 280 }}
          />
        </Group>

        {users.isLoading ? (
          <Center py="xl">
            <Loader color="invetrak" />
          </Center>
        ) : users.isError ? (
          <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
            {describeLoadFailure(users.error, t('users.errors.listFailed'))}
          </Alert>
        ) : visible.length === 0 ? (
          <Alert color="blue" variant="light" icon={<IconAlertCircle size={16} />}>
            {t('users.none')}
          </Alert>
        ) : (
          <ResponsiveTable
            rows={visible}
            columns={columns}
            rowKey={(u) => u.userId}
            minWidth={760}
            actionsWidth={60}
            striped
            highlightOnHover
            actions={(u) => (
              <Tooltip label={t('users.editAria', { name: u.displayName || u.email })}>
                <ActionIcon
                  variant="subtle"
                  color="invetrak"
                  aria-label={t('users.editAria', { name: u.displayName || u.email })}
                  onClick={() => setEditing(u)}
                >
                  <IconPencil size={16} />
                </ActionIcon>
              </Tooltip>
            )}
          />
        )}
      </Stack>

      {/* Under the users, because it is the shorter list and the temporary one. Somebody who has
          been invited is not yet a user, so mixing them into the table above would mean a row you
          cannot open and counts that mean nothing. */}
      {invitations.isError && (
        <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />} mt="md">
          {describeLoadFailure(invitations.error, t('users.errors.invitationsFailed'))}
        </Alert>
      )}

      {(invitations.data ?? []).length > 0 && (
        <Card withBorder mt="md">
          <Text fw={500} mb="xs">
            {t('users.pendingInvitations')}
          </Text>
          <Stack gap="xs">
            {(invitations.data ?? []).map((i: InvitationDto) => (
              <Group key={i.tenantInvitationId} justify="space-between" wrap="nowrap">
                <div style={{ minWidth: 0 }}>
                  <Text size="sm" truncate>
                    {i.email}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {t('users.inviteExpires', {
                      when: new Date(i.expiresAtUtc).toLocaleDateString(),
                    })}
                    {i.grants.length > 0 &&
                      ` · ${i.grants
                        .map((g) =>
                          g.warehouseId
                            ? g.groupCode
                            : `${g.groupCode} (${t('users.access.everywhere')})`,
                        )
                        .join(', ')}`}
                  </Text>
                </div>
                <Tooltip label={t('users.cancelInvite', { email: i.email })}>
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    aria-label={t('users.cancelInvite', { email: i.email })}
                    loading={cancel.isPending && cancel.variables === i.tenantInvitationId}
                    onClick={() => cancel.mutate(i.tenantInvitationId)}
                  >
                    <IconX size={16} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            ))}
          </Stack>
        </Card>
      )}

      <InviteUserModal
        opened={inviting}
        onClose={() => setInviting(false)}
        onInvited={(email) => {
          notifications.show({ message: t('users.inviteSent', { email }), color: 'green' });
          void queryClient.invalidateQueries({ queryKey: ['invitations'] });
        }}
      />

      <UserAccessModal
        user={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          void queryClient.invalidateQueries({ queryKey: ['users'] });
          void queryClient.invalidateQueries({ queryKey: ['user-groups'] });
        }}
      />
    </>
  );
}
